import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Armchair, House, Buildings, ArrowRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import RealisationsSection from "@/components/RealisationsSection";

const services = [
  {
    id: "auto",
    icon: Car,
    title: "Nettoyage Automobile",
    description: "Nettoyage intérieur et extérieur à domicile. Citadine, berline, SUV — tarification transparente.",
    link: "/property-services/automobile",
    cta: "Réserver",
  },
  {
    id: "mobilier",
    icon: Armchair,
    title: "Nettoyage Mobilier",
    description: "Tapis, canapés, matelas — nettoyage professionnel à domicile. À partir de 30 €.",
    link: "/property-services/mobilier",
    cta: "Demander un devis",
  },
  {
    id: "appartement",
    icon: House,
    title: "Nettoyage Appartement",
    description: "Ménage professionnel à l'heure. Matériel fourni par ALTURA ou par vos soins.",
    link: "/property-services/appartement",
    cta: "Réserver",
  },
  {
    id: "commercial",
    icon: Buildings,
    title: "Locaux Commerciaux",
    description: "Bureaux, commerces, espaces professionnels — intervention sur devis personnalisé.",
    link: "/property-services/commercial",
    cta: "Demander un devis",
  },
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function PropertyServicesPage() {
  return (
    <>
      <SEO
        title="ALTURA Property Services — Nettoyage professionnel"
        description="Nettoyage automobile, mobilier, appartement et locaux commerciaux. Service premium à domicile en Île-de-France."
      />

      {/* Hero */}
      <section className="bg-[#1A1A1A] py-20 sm:py-28" data-testid="property-hero">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[#D0CEC7] text-xs tracking-[0.3em] uppercase mb-4"
          >
            Property Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide mb-6"
          >
            Nettoyage professionnel<br />à domicile
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-white/60 text-sm sm:text-base max-w-xl mx-auto leading-relaxed"
          >
            Automobile, mobilier, appartement ou locaux commerciaux — des interventions soignées, 
            avec une tarification transparente et un service sur mesure.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-[#EDEBE4] py-16 sm:py-20" data-testid="property-services-grid">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={s.link}
                  className="block bg-white border border-[#1A1A1A]/10 p-8 hover:border-[#1A1A1A]/30 transition-all group"
                  data-testid={`property-card-${s.id}`}
                >
                  <s.icon size={28} weight="light" className="text-[#1A1A1A]/70 mb-4" />
                  <h2 className="font-heading text-lg text-[#1A1A1A] mb-2 tracking-wide">{s.title}</h2>
                  <p className="text-[#1A1A1A]/60 text-sm leading-relaxed mb-6">{s.description}</p>
                  <span className="inline-flex items-center gap-2 text-[#1A1A1A] text-xs tracking-[0.15em] uppercase group-hover:gap-3 transition-all">
                    {s.cta} <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] text-center mb-12 tracking-wide">
            Comment ça fonctionne
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Votre demande", desc: "Choisissez votre service, personnalisez votre intervention et obtenez un tarif immédiat." },
              { step: "02", title: "Confirmation", desc: "Notre équipe valide votre demande et confirme le créneau d'intervention." },
              { step: "03", title: "Intervention", desc: "Nos professionnels interviennent à l'adresse convenue, avec le matériel adapté." },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <p className="font-heading text-3xl text-[#1A1A1A]/10 mb-3">{s.step}</p>
                <h3 className="text-[#1A1A1A] text-sm font-medium tracking-wide uppercase mb-2">{s.title}</h3>
                <p className="text-[#1A1A1A]/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RealisationsSection />

      {/* SEO pages links */}
      <section className="bg-[#EDEBE4] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-lg text-[#1A1A1A] text-center mb-6">Nos services par ville</h2>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {[
              { to: "/property-services/nettoyage-auto-versailles", label: "Nettoyage auto — Versailles" },
              { to: "/property-services/nettoyage-auto-poissy", label: "Nettoyage auto — Poissy" },
              { to: "/property-services/nettoyage-auto-saint-germain-en-laye", label: "Nettoyage auto — Saint-Germain" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="flex items-center gap-2 bg-[#1A1A1A] text-white px-4 py-3 text-xs hover:bg-[#333] transition-colors group">
                <span className="flex-1">{l.label}</span>
                <ArrowRight size={12} className="text-white/30 group-hover:text-white" />
              </Link>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { to: "/property-services/nettoyage-auto-ile-de-france", label: "Nettoyage auto — Île-de-France" },
              { to: "/property-services/nettoyage-canape-tapis-ile-de-france", label: "Canapé & tapis — Île-de-France" },
              { to: "/property-services/menage-appartement-ile-de-france", label: "Ménage appartement — Île-de-France" },
              { to: "/property-services/nettoyage-locaux-professionnels-ile-de-france", label: "Locaux professionnels — Île-de-France" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="flex items-center gap-2 bg-white border border-[#1A1A1A]/10 px-4 py-3 text-xs text-[#1A1A1A] hover:border-[#1A1A1A]/30 transition-colors group">
                <span className="flex-1">{l.label}</span>
                <ArrowRight size={12} className="text-[#1A1A1A]/20 group-hover:text-[#1A1A1A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] py-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-white/80 text-sm mb-4">Une question ? Un besoin spécifique ?</p>
          <a
            href="https://wa.me/33681969342"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-[#1A1A1A] px-8 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#EDEBE4] transition-colors"
            data-testid="property-whatsapp-cta"
          >
            Nous contacter sur WhatsApp
          </a>
        </div>
      </section>
    </>
  );
}
